import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    VersionEventActorFieldsWithDefaults,
    VersionEventActorSchemaTypes,
} from "../../types/versionEventActor.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "VersionEventActor" as const;

/**
 * Kinds of actor that can trigger a version transition. Spelled `actorType` rather than `type`,
 * which mongoose reads as a path-type declaration.
 *
 * TODO: Add "system" — `{ actorType: "system"; subsystem: string }` — so internal writes (periodic
 * jobs, reconcilers, warning creation) name the subsystem responsible instead of leaving `changedBy`
 * absent. That means supplying a subsystem at every internal SCD write site, so it is its own piece
 * of work. "agent" and "technician" arms are likewise expected eventually.
 */
export const VERSION_EVENT_ACTOR_TYPES = ["user"] as const;

export type VersionEventActorType = (typeof VERSION_EVENT_ACTOR_TYPES)[number];

/**
 * Who triggered a version transition. Every stored actor carries `actorType`, so later arms are
 * additive — no backfill of existing events.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<VersionEventActor>>(...addSchemaKeyHook(SCHEMA_KEY))
export class VersionEventActor extends SchemaBase<VersionEventActor, VersionEventActorFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.enum(VERSION_EVENT_ACTOR_TYPES) })
    @prop({ type: () => String, required: true, enum: VERSION_EVENT_ACTOR_TYPES })
    public actorType!: VersionEventActorType;

    /** Descope user id, an opaque string such as `U2abc...`. */
    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public descopeUserId!: string;

    static getters = {} as const satisfies Record<string, (self: VersionEventActor) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<VersionEventActorSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<VersionEventActor, VersionEventActorFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

VersionEventActor.dtoValidators = getZodSchemaFromClass(
    VersionEventActor,
) as unknown as DtoValidatorTypes<VersionEventActorSchemaTypes>;
